import { ClinicSettings } from "@/Common/Api/MedfarDictionaries/Dto/Responses/ClinicSettings";
import { FlatLookupView } from "@/Common/Api/MedfarDictionaries/Dto/Responses/FlatLookupView";
import { LicenceOrganisationRule } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationRule";
import { LicenceOrganisationSpecialisation } from "@/Common/Api/MedfarDictionaries/Dto/Responses/LicenceOrganisationSpecialisation";
import { BaseReactComponent } from "@/Common/Components/BaseReactComponent";
import { SelectLookupControl } from "@/Common/Components/Inputs/SelectLookupControl";
import { TextboxControl } from "@/Common/Components/Inputs/TextboxControl";
import { EmployeeUIModel } from "@/Common/Models/UI/Employee/EmployeeUIModel";
import { ExternalBillingPaymentMode } from "@/Common/Resources/Lookups/Enums/CommonLookupCodes";
import { FilteredOrganizationRules } from "@/Common/Utilities/FilteredOrganizationRules";
import { cacheStore } from "@/Globals/Stores/CacheStore";
import { SelectedOrganizationValueIdentifier } from "./SelectedOrganizationValueIdentifier";
import { BaseEditableUserInfoPanelProps } from "./UserDetailsPanelComponent";

class UserPracticeInfoEditComponentState {
    public flatLookupRemunaration: FlatLookupView[];
    public canSaveUserPracticeInfo: boolean;
    public selectedOrganizationValueIdentifier: string;
}

interface UserPracticeInfoEditComponentProps extends BaseEditableUserInfoPanelProps {
    clinicSettings: ClinicSettings;
}

class UserPracticeInfoEditComponent extends BaseReactComponent<UserPracticeInfoEditComponentProps, UserPracticeInfoEditComponentState> {
    private readonly c1Paddingtop: React.CSSProperties = { paddingTop: "5px" };
    private readonly c2Width: React.CSSProperties = { width: "50%" };
    private readonly noOrganizationCode = "-";
    private readonly filteredOrganizationRules: FilteredOrganizationRules = new FilteredOrganizationRules(cacheStore.getFilteredOrganizationRulesParam());
    private specialtyOptions: LicenceOrganisationSpecialisation[];

    constructor(props: UserPracticeInfoEditComponentProps) {
        super(props);

        // Set initial state
        const initialState = new UserPracticeInfoEditComponentState();
        initialState.flatLookupRemunaration = cacheStore.FindLookupsByTypeName("RemunerationTypeCode");
        this.state = initialState;
    }

    public componentDidUpdate(prevProps: UserPracticeInfoEditComponentProps): void {
        const oldUser = prevProps.editedUser?.Model;
        const newUser = this.props.editedUser?.Model;

        const isOrganizationRuleModified = oldUser?.Licences[0]?.IssuingLicenseOrganizationRuleId !== newUser?.Licences[0]?.IssuingLicenseOrganizationRuleId;
        const isSpecialtyModified = oldUser?.LicenseOrganizationSpecializationId !== newUser?.LicenseOrganizationSpecializationId;

        if (isOrganizationRuleModified || isSpecialtyModified) {
            this.initTitleAndSpecialtySelection(this.props);
        }
    }

    public componentDidMount(): void {
        $(window).trigger("resize");

        this.initTitleAndSpecialtySelection(this.props);
    }

    public render(): JSX.Element {
        return (
            <div>
                <div className="user-component">
                    <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                        <div className="h">
                            <h3 className="thin-font">{cacheStore.GetString("PracticeInformationLabel")}</h3>
                        </div>
                    </div>
                    <div className="psingle-column practice centered" style={{ "margin": "initial" }}>
                        <div className="tb">
                            {this.renderPractice(this.props.editedUser)}
                        </div>
                    </div>
                </div>

                <div className="user-component">
                    <div className="flex-container heading" style={{ "borderBottom": "solid 1px #ccc", "margin": "20px 25px" }}>
                        <div className="h">
                            <h3 className="thin-font">{cacheStore.GetString("BillingMenuLabel")}</h3>
                        </div>
                    </div>
                    <div className="psingle-column practice centered" style={{ "margin": "initial" }}>
                        <div className="tb">
                            {this.renderFacturation(this.props.editedUser)}
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    private renderPractice(employee: EmployeeUIModel): JSX.Element {
        const showOrgSelection = this.showOrgSelection(this.props.editedUser);
        const isValidOrganizationSelection = this.isValidOrganizationSelection();

        return (
            <div className="tbc">
                <div>
                    <table>
                        <tbody>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}>
                                    <label className="form-label">
                                        {cacheStore.GetString("PermitNumberLabel")}
                                    </label>
                                </td>
                                <td className="c2" style={this.c2Width}>
                                    <TextboxControl
                                        id="PermitNumberEditAdd"
                                        value={employee.Model.Licences?.[0]?.LicenceNumber ?? ""}
                                        className="form-control"
                                        onChange={e => this.props.mutateParentEmployee(emp => emp.setPermitNumber(e))}
                                    />
                                </td>
                            </tr>
                            {showOrgSelection && (
                                <>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("OrganizationLabel")}</label></td>
                                        <td className="c2" style={this.c2Width}>
                                            {this.renderOrganizationSelect()}
                                        </td>
                                    </tr>
                                    {!isValidOrganizationSelection && (
                                        <tr>
                                            <td className="c1" style={this.c1Paddingtop} />
                                            <td className="c2 red-text" style={this.c2Width}>
                                                {cacheStore.GetString("SelectSpecificOrganizationLabel")}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                            {this.showTitleSelection() && (
                                <>
                                    <tr>
                                        <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("TitleLabel")}</label></td>
                                        <td className="c2">
                                            {this.renderTitleSelect()}
                                        </td>
                                    </tr>
                                    {!this.isTitleValid(this.props) && (!showOrgSelection || isValidOrganizationSelection) && (
                                        <tr>
                                            <td className="c1" style={this.c1Paddingtop} />
                                            <td className="c2 red-text" style={this.c2Width}>
                                                {cacheStore.GetString("SelectSpecificTitleLabel")}
                                            </td>
                                        </tr>
                                    )}
                                </>
                            )}
                            {this.showSpecialtySelection() && (
                                <tr>
                                    <td className="c1" style={this.c1Paddingtop}>
                                        <label className="form-label">
                                            {cacheStore.GetString("SpecialtyLabel")}
                                        </label>
                                    </td>
                                    <td className="c2" style={this.c2Width}>
                                        {this.renderSpecialtySelect()}
                                    </td>
                                </tr>
                            )}
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("IssuingUniversityLabel")}</label></td>
                                <td className="c2" style={this.c2Width}>
                                    <TextboxControl
                                        id="UniversityEditAdd"
                                        value={employee.Model.University}
                                        className="form-control"
                                        maxLength={300}
                                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.University = e)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("StatusLabel")}</label></td>
                                <td className="c2" style={this.c2Width}>
                                    <SelectLookupControl
                                        IncludeEmptyChoice
                                        Value={employee.Model.StatusLookupId ?? ""}
                                        AvailableValues={cacheStore.GetClinicianPermitStatusLookups().map(x => x.ToComboboxItem())}
                                        Callback={e => this.props.mutateParentEmployee(emp => emp.Model.StatusLookupId = e)}
                                    />
                                </td>
                            </tr>
                            <tr>
                                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PermitTypeLabel")}</label></td>
                                <td className="c2" style={this.c2Width}>
                                    <SelectLookupControl
                                        IncludeEmptyChoice
                                        Value={(employee.Model.Licences?.[0]?.PermitTypeLookupId ?? "")}
                                        AvailableValues={cacheStore.GetClinicianPermitTypeLookups().map(x => x.ToComboboxItem())}
                                        Callback={e => this.props.mutateParentEmployee(emp => emp.setpermitType(e))}
                                        disabled={!employee.Model.Licences?.[0]?.LicenceNumber}
                                    />
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderFacturation(employee: EmployeeUIModel): JSX.Element {
        let billingRows: JSX.Element[];

        switch (employee.getBillingRegionIsoCode()) {
            case "CA-QC":
                billingRows = this.renderQuebecBillingRows(employee);
                break;

            case "CA-BC":
                billingRows = this.renderBCBillingRows(employee);
                break;

            default:
                billingRows = this.renderGenericBillingRows(employee);
                break;
        }

        return (
            <div className="tbc">
                <div>
                    <table>
                        <tbody>
                            {billingRows}
                            {this.canRenderPaymentModeRow() && this.renderPaymentModeRow(this.props.editedUser)}
                        </tbody>
                    </table>
                </div>
            </div>
        );
    }

    private renderQuebecBillingRows(employee: EmployeeUIModel): JSX.Element[] {
        return [
            <tr key="RamqNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("RAMQNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="RQMQNumberEditAdd"
                        IsNumeric
                        value={employee.Model.RamqNumber}
                        maxLength={150}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.RamqNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="RemunerationTypeLookupId">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("RemunerationTypeLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <SelectLookupControl
                        IncludeEmptyChoice
                        Value={employee.Model.RemunerationTypeLookupId}
                        AvailableValues={this.state.flatLookupRemunaration.map(x => x.ToComboboxItem())}
                        Callback={e => this.props.mutateParentEmployee(emp => emp.Model.RemunerationTypeLookupId = e)}
                    />
                </td>
            </tr>,
            <tr key="ProfessionalNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("ProfessionalNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="ProfessionalNumberEditAdd"
                        IsNumeric
                        value={employee.Model.ProfessionalNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.ProfessionalNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="GroupNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("GroupNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="GroupNumberEditAdd"
                        IsNumeric
                        value={employee.Model.GroupNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.GroupNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="DispensationNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("DispensationNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="DispensationNumberEditAdd"
                        IsNumeric
                        value={employee.Model.DispensationNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.DispensationNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="AdministrativeAccountNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("AdministrativeAccountNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="AdministrativeAccountNumberEditAdd"
                        IsNumeric
                        value={employee.Model.AdministrativeAccountNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.AdministrativeAccountNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="BillingAgencyNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("BillingAgencyNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="BillingAgencyNumberEditAdd"
                        IsNumeric
                        value={employee.Model.BillingAgencyNumber}
                        maxLength={50}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.BillingAgencyNumber = e)}
                    />
                </td>
            </tr>
        ];
    }

    private renderBCBillingRows(employee: EmployeeUIModel): JSX.Element[] {
        return [
            <tr key="ProfessionalNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("MspPractitionerNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="ProfessionalNumberEditAdd"
                        value={employee.Model.ProfessionalNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.ProfessionalNumber = e)}
                    />
                </td>
            </tr>,
            <tr key="BillingAgencyNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("MspPayeeNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="BillingAgencyNumberEditAdd"
                        value={employee.Model.BillingAgencyNumber}
                        maxLength={50}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.BillingAgencyNumber = e)}
                    />
                </td>
            </tr>
        ];
    }

    private renderGenericBillingRows(employee: EmployeeUIModel): JSX.Element[] {
        return [
            <tr key="ProfessionalNumber">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("ProfessionalNumberLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <TextboxControl
                        id="ProfessionalNumberEditAdd"
                        IsNumeric
                        value={employee.Model.ProfessionalNumber}
                        className="form-control"
                        onChange={e => this.props.mutateParentEmployee(emp => emp.Model.ProfessionalNumber = e)}
                    />
                </td>
            </tr>
        ];
    }

    private renderPaymentModeRow(employee: EmployeeUIModel): JSX.Element {
        return (
            <tr key="PaymentMode">
                <td className="c1" style={this.c1Paddingtop}><label className="form-label">{cacheStore.GetString("PaymentModeLabel")}</label></td>
                <td className="c2" style={this.c2Width}>
                    <SelectLookupControl
                        Value={employee.Model.ExternalBillingPaymentModeLookupId}
                        AvailableValues={cacheStore.GetExternalBillingPaymentModeLookups().map(x => x.ToComboboxItem())}
                        Callback={(e: string) => this.props.mutateParentEmployee(emp => emp.Model.ExternalBillingPaymentModeLookupId = e)}
                    />
                </td>
            </tr>
        );
    }

    private renderOrganizationSelect(): JSX.Element {
        const className = `form-control myle-control ellipsis-auto width-100 ${!this.isValidOrganizationSelection() ? " invalid" : ""}`;

        return (
            <div>
                <select
                    id="licensedOrganisationSelect"
                    onChange={e => this.onOrganizationSelect(e)}
                    className={className}
                    value={this.state.selectedOrganizationValueIdentifier}
                    title={this.filteredOrganizationRules.getOrganizationAcronymAndNameById(this.state.selectedOrganizationValueIdentifier)}
                    data-cy="userProfile-practiceOrganization"
                >
                    <option />
                    <option
                        key={this.noOrganizationCode}
                        value={this.noOrganizationCode}
                    >
                        -
                    </option>
                    {this.renderOrganizationsSelectableOptions()}
                </select>
            </div>
        );
    }

    private renderOrganizationsSelectableOptions(): JSX.Element[] {
        const countryCodes = this.filteredOrganizationRules.getCountryCodes();

        if (countryCodes.length === 1)
            return this.renderOrganizationsSelectableOptionsByState(countryCodes[0]);

        return countryCodes.map(countryCode => (
            <optgroup
                key={countryCode}
                label={this.filteredOrganizationRules.getCountryLabel(countryCode)}
            >
                {this.renderOrganizationsSelectableOptionsByState(countryCode)}
            </optgroup>
        ));
    }

    private renderOrganizationsSelectableOptionsByState(countryCode: string): JSX.Element[] {
        const stateCodes = this.filteredOrganizationRules.getStateCodes(countryCode);

        return stateCodes.map(stateCode => (
            <optgroup
                key={`${countryCode}_${stateCode}`}
                label={this.filteredOrganizationRules.getStateLabel(countryCode, stateCode)}
            >
                {this
                    .filteredOrganizationRules
                    .getDistinctOrganizations(countryCode, stateCode)
                    .map(p => this.getOrganizationOption(p))
                }
            </optgroup>
        ));
    }

    private initTitleAndSpecialtySelection(props: BaseEditableUserInfoPanelProps): void {
        const userLicenceOrganizationRule = props.editedUser.getIssuingOrgRule();
        const userActiveSite = props.editedUser?.getActiveSitePrimaryAddress();

        this.filteredOrganizationRules.createLicenceOrganizationRulesByCountryAndState(
            null,
            userActiveSite?.CountryLookupId,
            userActiveSite?.StateLookupId
        );

        this.mutateState(
            s => {
                if (userLicenceOrganizationRule)
                    s.selectedOrganizationValueIdentifier = this.getCurrentOrganizationIdentifier(props);

                s.canSaveUserPracticeInfo = this.isValidLicenceOrganizationRule(props) && this.isTitleValid(props);
            }
        );

        this.setSpecialtyOptions(userLicenceOrganizationRule?.Id);
        props.isValidCallBack?.(this.isValidLicenceOrganizationRule(props) && this.isTitleValid(props));
    }

    private getOrganizationOption(licenseOrganizationRule: LicenceOrganisationRule): JSX.Element {
        return (
            <option
                key={licenseOrganizationRule.Id}
                value={SelectedOrganizationValueIdentifier.generateIdentifier(licenseOrganizationRule)}
            >
                {LicenceOrganisationRule.getOrganizationAcronymAndName(licenseOrganizationRule)}
            </option>
        );
    }

    private onOrganizationSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const orgIdentifier = e.target.value;
        this.props.mutateParentEmployee(emp => {
            emp.setIssuingOrgRule("");
            emp.Model.LicenseOrganizationSpecializationId = "";
            emp.Model.ExternalBillingPaymentModeLookupId = this.getExternalBillingPaymentModeLookupId(orgIdentifier, emp.Model.ExternalBillingPaymentModeLookupId);
        });
        this.mutateState(s => s.selectedOrganizationValueIdentifier = orgIdentifier);
    }

    private getExternalBillingPaymentModeLookupId(orgIdentifier: string, externalBillingPaymentModeLookupId: string): string {
        if (!cacheStore.ClinicSettings.EnableExternalBillingPaymentMode)
            return externalBillingPaymentModeLookupId;

        if (!(orgIdentifier?.includes("CA_ON") || orgIdentifier?.includes("CA_BC")))
            return null;

        return externalBillingPaymentModeLookupId
            ?? cacheStore.FindLookupByLookupCode(ExternalBillingPaymentMode.RegularPaymentMode).LookupID;
    }

    private renderTitleSelect(): JSX.Element {
        const className = `form-control myle-control ${!this.isTitleValid(this.props) ? " invalid" : ""}`;

        return (
            <div>
                <select
                    id="titleOrganisationSelect"
                    onChange={e => this.onTitleSelect(e)}
                    className={className}
                    value={this.props.editedUser.getIssuingOrgRule()?.Id ?? ""}
                    data-cy="userProfile-practiceTitle"
                >
                    <option />
                    {this.renderTitleSelectableOptions()}
                </select>
            </div>
        );
    }

    private renderTitleSelectableOptions(): JSX.Element[] {
        let organizationRuleTitleOptions: LicenceOrganisationRule[];

        if (this.state.selectedOrganizationValueIdentifier === this.noOrganizationCode || !this.state.selectedOrganizationValueIdentifier) {
            organizationRuleTitleOptions = this.filteredOrganizationRules.getNoOrganizationTitles();
        }
        else {
            const org = SelectedOrganizationValueIdentifier.parseIdentifier(this.state.selectedOrganizationValueIdentifier);
            organizationRuleTitleOptions = this.filteredOrganizationRules.getTitlesOfOrganization(org.orgCode, org.countryCode, org.stateCode);
        }

        return organizationRuleTitleOptions.map(p => this.getTitleOption(p));
    }

    private getTitleOption(organizationRule: LicenceOrganisationRule): JSX.Element {
        return (
            <option
                key={organizationRule.Id}
                value={organizationRule.Id}
            >
                {LicenceOrganisationRule.getTitleName(organizationRule)}
            </option>
        );
    }

    private onTitleSelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const licenceOrganizationRuleId = e.target.value;

        // update specialty options according to new title chosen
        this.setSpecialtyOptions(licenceOrganizationRuleId);

        this.props.mutateParentEmployee(emp => {
            emp.setIssuingOrgRule(licenceOrganizationRuleId);

            // Autocomplete specialty if only one licenceOrgSpecialty mapped to the chosen licenceOrgRule
            emp.Model.LicenseOrganizationSpecializationId = this.specialtyOptions?.length == 1
                ? this.specialtyOptions[0]?.Id
                : "";
        });
    }

    private renderSpecialtySelect(): JSX.Element {
        let classname = "form-control myle-control";
        return (
            <div>
                <select
                    id="LicenseOrganizationSpecialization"
                    onChange={e => this.onSpecialtySelect(e)}
                    className={classname}
                    value={this.props.editedUser.Model.LicenseOrganizationSpecializationId ?? ""}
                    data-cy="userProfile-practiceSpecialty"
                >
                    <option />
                    {this.renderSpecialtySelectableOptions()}
                </select>
            </div>
        );
    }

    private onSpecialtySelect(e: React.ChangeEvent<HTMLSelectElement>): void {
        const licenseOrganizationSpecializationId = e.target.value;
        this.props.mutateParentEmployee(emp => emp.Model.LicenseOrganizationSpecializationId = licenseOrganizationSpecializationId);
    }

    private renderSpecialtySelectableOptions(): JSX.Element[] {
        return this.specialtyOptions.map(p => this.getSpecialtyOption(p));
    }

    private getSpecialtyOption(licenseOrganizationSpecialization: LicenceOrganisationSpecialisation): JSX.Element {
        return (
            <option
                key={licenseOrganizationSpecialization.Id}
                value={licenseOrganizationSpecialization.Id}
            >
                {LicenceOrganisationSpecialisation.getSpecialtyName(licenseOrganizationSpecialization)}
            </option>
        );
    }

    private isValidOrganizationSelection(): boolean {
        return !IsNullOrWhiteSpace(this.state.selectedOrganizationValueIdentifier);
    }

    private isTitleValid(props: BaseEditableUserInfoPanelProps): boolean {
        return props.editedUser.getIssuingOrgRule()?.isActive;
    }

    private isValidLicenceOrganizationRule(props: BaseEditableUserInfoPanelProps): boolean {
        const userLicenceOrganizationRule = props.editedUser?.getIssuingOrgRule();

        return this.filteredOrganizationRules.isOrganizationInAvailableOrganizationRules(userLicenceOrganizationRule);
    }

    private setSpecialtyOptions(orgRuleId: string): void {
        this.specialtyOptions = cacheStore.TitlesAndSpecialtiesCache
            .filter(p => p.Model.licenceOrganisationRuleModel.Id == orgRuleId && p.Model.licenceOrganisationSpecialisationModel.IsActive)
            .map(p => p.Model.licenceOrganisationSpecialisationModel)
            .sortBy(p => LicenceOrganisationSpecialisation.getSpecialtyName(p))
            .toArray();
    }

    private showOrgSelection(editedUser: EmployeeUIModel): boolean {
        return this.filteredOrganizationRules.envHasLinkedOrgRules || !!editedUser?.getIssuingOrgRule()?.OrganisationCode;
    }

    private showTitleSelection(): boolean {
        return this.filteredOrganizationRules.envHasLinkedOrgRules
            ? this.isValidOrganizationSelection()
            : this.filteredOrganizationRules.envHasNotLinkedOrgRules;
    }

    private showSpecialtySelection(): boolean {
        return this.isTitleValid(this.props) && this.specialtyOptions?.length > 0;
    }

    private getCurrentOrganizationIdentifier(props: BaseEditableUserInfoPanelProps): string {
        if (!props.editedUser?.getIssuingOrgRule()?.OrganisationCode && this.isValidLicenceOrganizationRule(props))
            return this.noOrganizationCode;

        if (this.isValidLicenceOrganizationRule(props))
            return SelectedOrganizationValueIdentifier.generateIdentifier(props.editedUser.getIssuingOrgRule());

        return "";
    }

    private canRenderPaymentModeRow(): boolean {
        return cacheStore.ClinicSettings.EnableExternalBillingPaymentMode &&
            (this.state.selectedOrganizationValueIdentifier?.includes("CA_BC") ||
                this.state.selectedOrganizationValueIdentifier?.includes("CA_ON"));
    }
}

export { UserPracticeInfoEditComponent };
